/* eslint-disable @typescript-eslint/no-non-null-assertion,no-underscore-dangle  */
import {CollectionFactory, Reducer} from '@modifi/redux'
import {
  OnboardingTradePartnerView,
  getAuthenticatedThemisClient,
} from '@modifi/onboarding-api-clients'
import {createSelector} from 'reselect'
import {TradePartnerApprovalStatus} from '@modifi/enums'

const apiClient = getAuthenticatedThemisClient()

export type ExtendedOnboardingTradePartnerView = OnboardingTradePartnerView & {
  _id?: string
}

const {actions, reducer, selectors} = CollectionFactory({
  actionNamePrefix: 'data/seller-trade-partners/collection',
  reducerPath: ['data', 'sellerTradePartners', 'collection'],
  entityPrimaryKeySelector: (entity: ExtendedOnboardingTradePartnerView) => entity._id!,
  fetchRequest: () =>
    apiClient.TradePartners.fetchBuyers({orderBy: ['createdOn'], orderDirection: ['DESC']}).then(
      res => {
        const tradePartners = res.data()?.tradePartners || []
        return (
          tradePartners.map(tradePartner => ({
            _id: tradePartner?.tradePartnerCompany?.externalCompanyId as string,
            ...tradePartner,
          })) || []
        )
      }
    ),
})

export default reducer as Reducer

export const selectSellerTradePartners = (
  state: Record<string, any>
): ExtendedOnboardingTradePartnerView[] =>
  selectors.selectByKeyWindow(state, 'all') as ExtendedOnboardingTradePartnerView[]

const selectSellerTradePartnersFetchCount = (state: Record<string, any>): number =>
  selectors.selectFetchCountByKeyWindow(state, 'all')

export const selectSellerTradePartnersWhereLoaded = createSelector(
  selectSellerTradePartnersFetchCount,
  fetchCount => fetchCount > 0
)

export const selectSellerTradePartnersCount = createSelector(
  selectSellerTradePartners,
  tradePartners => tradePartners.length
)

export const selectSellerTradePartnersAreLoading = (state: Record<string, any>): boolean =>
  selectors.selectIsKeyWindowLoading(state, 'all')

export const selectSellerTradePartnerByExternalId = (
  state: Record<string, any>,
  externalTradePartnerId: string
): ExtendedOnboardingTradePartnerView | undefined =>
  (selectors.selectById(state, externalTradePartnerId) || undefined) as
    | ExtendedOnboardingTradePartnerView
    | undefined

export const selectSellerTradePartnerNameByExternalId = createSelector(
  selectSellerTradePartnerByExternalId,
  sellerTradePartner => sellerTradePartner?.tradePartnerCompany?.address?.companyName || ''
)

export const selectSellerTradePartnerApprovalStatus = createSelector(
  selectSellerTradePartnerByExternalId,
  sellerTradePartner => sellerTradePartner?.tradePartnerApproval?.status || ''
)

export const aFetchSellerTradePartners = (): any => actions!.fetch('all')

type NonNullableTradePartnerExternalCompanyId = NonNullable<
  NonNullable<ExtendedOnboardingTradePartnerView['company']>['externalCompanyId']
>

export const aUpdateLocalSellerTradePartner = (
  tradePartner: ExtendedOnboardingTradePartnerView & {
    company: {
      externalCompanyId: NonNullableTradePartnerExternalCompanyId
    }
  },
  merge = true
): any =>
  actions!.update(
    {
      _id: tradePartner.company.externalCompanyId,
      ...tradePartner,
    },
    merge
  )

export const selectCanUpdateTradePartnerCompanyDetails = createSelector(
  selectSellerTradePartnerByExternalId,
  tradePartner => !!tradePartner?.actions?.canUpdateTradePartnerCompanyDetails
)

export const selectSellerTradePartnerContactPersons = createSelector(
  selectSellerTradePartnerByExternalId,
  tradePartner => tradePartner?.tradePartnerCompany?.users || []
)

export const selectCanUpdateSellerTradePartnerContactPersons = createSelector(
  selectSellerTradePartnerByExternalId,
  tradePartner => !!tradePartner?.actions?.canUpdateUsers
)

export const selectCanUpdateSellerTradePartnerDataInfo = createSelector(
  selectSellerTradePartnerByExternalId,
  tradePartner => tradePartner?.actions?.canUpdateTradePartnerInfo
)

export const selectSellerTradePartnerAllocatedLimit = createSelector(
  selectSellerTradePartnerByExternalId,
  tradePartner => tradePartner?.allocatedLimit
)

export const selectCanEditTradePartner = createSelector(
  selectSellerTradePartnerApprovalStatus,
  approvalStatus =>
    [TradePartnerApprovalStatus.PENDING, TradePartnerApprovalStatus.LIMIT_AVAILABLE].includes(
      approvalStatus as TradePartnerApprovalStatus
    )
)

export const selectSumOfSellerTradePartnerLimits = createSelector(
  selectSellerTradePartners,
  tradePartners =>
    tradePartners.reduce((sum, tradePartner) => {
      const allocatedLimit = +(tradePartner?.allocatedLimit?.amount || 0)
      return sum + (allocatedLimit > 0 ? allocatedLimit : 0)
    }, 0)
)

export const selectAnnualFundingPotentialForCompany = createSelector(
  selectSellerTradePartners,
  tradePartners =>
    tradePartners.reduce((sum, tradePartner) => {
      const allocatedLimit = +(tradePartner?.allocatedLimit?.amount || 0)
      const paymentTerm = tradePartner?.maximumPaymentTerm

      return sum + (allocatedLimit * 365) / (paymentTerm || 30)
    }, 0)
)

export const selectSellerTradePartnerLimitCurrency = createSelector(
  selectSellerTradePartners,
  tradePartners =>
    tradePartners?.find(tradePartner => !!tradePartner?.allocatedLimit?.currency)?.allocatedLimit
      ?.currency || ''
)
